import {
  compareDates,
  compareNumbers,
  createStringSearchFilter,
} from 'apollo-react/components/Table';
import _ from 'lodash';
import React from 'react';

import DataSourceDropdownFilter from '../../../components/TableFilters/DataSourceDropdownFilter';
import DatePickerFilter from '../../../components/TableFilters/DatePickerFilter';
import DropdownFilter from '../../../components/TableFilters/DropdownFilter';
import IntegerFilter from '../../../components/TableFilters/IntegerFilter';
import TextInputFilter from '../../../components/TableFilters/TextInputFilter';
import { getUITA } from '../../../utils/commonUtils';
import { createCustomDateFilter } from '../../../utils/formHistoryUtils';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';

export const compareBooleans = (x, y) => (x === y ? 0 : x ? -1 : 1);

export const getBooleanString = (value) =>
  typeof value === 'boolean' && value ? 'Yes' : typeof value === 'boolean' && !value ? 'No' : '';

export const compareBytes = (accessor, sortOrder) => (rowA, rowB) => {
  const stringA = getBooleanString(rowA[accessor]);
  const stringB = getBooleanString(rowB[accessor]);
  let result;

  if (stringA < stringB) {
    result = -1;
  } else if (stringA > stringB) {
    result = 1;
  } else {
    return 0;
  }

  return sortOrder === 'asc' ? result : -result;
};

export const createCustomBooleanSearchFilter = (accessor) => (row, filters) => {
  if (typeof filters[accessor] !== 'boolean') {
    return true;
  }

  return row[accessor] === filters[accessor];
};

export const createCustomNumberSearchFilter = (accessor) => (row, filters) => {
  if (!filters[accessor]) {
    return true;
  }
  if (isNilOrEmpty(row[accessor])) {
    return false;
  }
  const rowVal = row[accessor].toString();
  const filterVal = filters[accessor].toString();
  return rowVal.includes(filterVal);
};

export function compareStrings(accessor, sortOrder) {
  return function(rowA, rowB) {
    const stringA = rowA?.[accessor]?.toUpperCase() ?? '';
    const stringB = rowB?.[accessor]?.toUpperCase() ?? '';

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'variant',
      caseFirst: 'lower',
    });
    const result = collator.compare(stringA, stringB);

    const checkResult = Math.sign(result);

    if (sortOrder === 'asc') {
      return result;
    } else {
      return checkResult === 1 ? -1 : checkResult === -1 ? 1 : 0;
    }
  };
}

export const getSortFunction = (dataType) => {
  switch (dataType) {
    case 'System.Int32':
    case 'System.Decimal':
      return compareNumbers;
    case 'System.String':
      return compareStrings;
    case 'System.DateTime':
      return compareDates;
    case 'System.Boolean':
      return compareBooleans;
    case 'System.Byte':
      return compareBytes;

    default:
      return;
  }
};

export const getFilterFunctionByDataType = ({ dataType, accessor }) => {
  switch (dataType) {
    case 'System.Int32':
    case 'System.Decimal':
      return createCustomNumberSearchFilter(accessor);
    case 'System.String':
      return createStringSearchFilter(accessor);
    case 'System.DateTime':
      return createCustomDateFilter(accessor);
    case 'System.Boolean':
    case 'System.Byte':
      return createCustomBooleanSearchFilter(accessor);

    default:
      return;
  }
};

export const getFilterComponentByDataType = ({ dataType, hasDataSourceChild }) => {
  if (hasDataSourceChild) {
    return DataSourceDropdownFilter;
  }

  switch (dataType) {
    case 'System.Int32':
    case 'System.Decimal':
      return IntegerFilter;
    case 'System.String':
      return TextInputFilter;
    case 'System.DateTime':
      return DatePickerFilter;
    case 'System.Boolean':
    case 'System.Byte':
      return DropdownFilter;

    default:
      return;
  }
};

export const getSelectedSubGridTitle = (title, rowsCount, rowIndex, translateString) => {
  const regexp = /\(\d+\)/;
  const titleWithoutCount = regexp.test(title) ? title.split(' (')[0] : title;

  return `${titleWithoutCount} (${rowIndex + 1} ${translateString('of')} ${rowsCount})`;
};

export const createSelectedSubGridObject = ({
  row,
  title,
  isViewOnly,
  disableEditFilter,
  translateString,
  ...rest
}) => {
  const { key, index, gridArrIndex, entityName, rowsLength, uiRowIndex } = row;
  const isParentlessTable = Array.isArray(gridArrIndex);

  return {
    rowId: key,
    row: {
      entityName,
      expanded: true,
    },
    title: getSelectedSubGridTitle(title, rowsLength, uiRowIndex, translateString),
    viewOnly: isViewOnly || disableEditFilter[isParentlessTable ? gridArrIndex : index],
    entityName,
    isParentlessTable,
    ...rest,
  };
};

export const getTablePaginationProps = (translateString) => ({
  pageText: translateString('Page'),
  // eslint-disable-next-line react/display-name
  labelDisplayedRows: ({ from, to, count }) => (
    <span style={{ whiteSpace: 'nowrap' }} ui-ta={getUITA('page-number')}>
      {`${
        count > 1 ? translateString('Items') : translateString('Item')
      } ${from}-${to} ${translateString('of')} ${count}`}
    </span>
  ),
  labelRowsPerPage: translateString('Rows'),
  ofText: translateString('of'),
  nextButtonText: translateString('Next page'),
  lastButtonText: translateString('Last page'),
  backButtonText: translateString('Previous page'),
  firstButtonText: translateString('First page'),
  'ui-ta': getUITA('internal-pagination'),
  backButtonProps: {
    'ui-ta': getUITA('back'),
  },
  nextButtonProps: {
    'ui-ta': getUITA('next'),
  },
  SelectProps: {
    'ui-ta': getUITA('pagination-select'),
  },
  firstPageButtonProps: {
    'ui-ta': getUITA('first-page'),
  },
  lastPageButtonProps: {
    'ui-ta': getUITA('last-page'),
  },
  pageNumberInputProps: {
    'ui-ta': getUITA('pagination-select'),
    InputProps: {
      inputProps: {
        'aria-label': 'Page Number',
      },
    },
  },
  dir: 'ltr',
});

export const getInModalTitle = (title, rowsCount) => {
  const regexp = /\(\d+\)/;

  if (regexp.test(title)) {
    return title.replace(regexp, `(${rowsCount})`);
  }

  return title;
};

export const getBuildNumbers = (selectedBuilds) => {
  const builds = selectedBuilds.map(({ buildNumber }) => buildNumber);
  builds.sort((a, b) => Number(a) - Number(b));

  return { buildNumberA: builds[0], buildNumberB: builds[1] };
};

export const getHighestPrintOrderNumber = (isBatchDocuments, rows) => {
  if (!isBatchDocuments || !rows.length) {
    return 0;
  }

  const printOrderNubmers = rows
    .filter((r) => typeof r.DBP_PRINT_ORDER === 'number')
    .map((r) => r.DBP_PRINT_ORDER);

  return printOrderNubmers.length ? _.max(printOrderNubmers) : 0;
};
